<template>
  <div class="lla-home">

    <!-- Cabecera: saludo + identidad del usuario -->
    <div class="lla-page-header">
      <div>
        <h1 class="lla-page-title">{{ saludo }}<span v-if="userName">, {{ userName }}</span></h1>
        <div class="lla-page-subtitle">{{ fechaLarga }}</div>
      </div>

      <div v-if="user && user.user" class="lla-page-actions">
        <v-chip
          small
          :color="user.user.active !== false ? 'success' : 'grey darken-2'"
          dark
        >
          <span class="lla-dot" :class="user.user.active !== false ? 'lla-dot--ok' : 'lla-dot--off'"></span>
          {{ user.user.active !== false ? 'Activo' : 'Inactivo' }}
        </v-chip>
        <v-chip
          v-for="rol in rolesUsuario"
          :key="rol"
          small
          :color="getRoleColor(rol)"
          dark
        >{{ rol }}</v-chip>
      </div>
    </div>

    <!-- Aviso de solicitudes pendientes -->
    <v-alert
      v-if="puedeAprobarAfiliaciones && pendingAffiliates && pendingAffiliates.length > 0"
      type="warning"
      dense
      text
      border="left"
      class="mb-5"
    >
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
        <span>
          Hay <strong>{{ pendingAffiliates.length }}</strong>
          solicitud{{ pendingAffiliates.length !== 1 ? 'es' : '' }} de afiliación
          esperando aprobación.
        </span>
        <v-btn small color="warning" dark to="/pendientes-aprobacion">
          <v-icon small left>visibility</v-icon>Revisar
        </v-btn>
      </div>
    </v-alert>

    <!-- Indicadores por categoría de afiliado -->
    <div class="lla-section-label">Afiliados por categoría</div>
    <Indicadores :items="items" class="mb-8" />

    <!-- Evolución de afiliaciones -->
    <v-card class="pa-2">
      <ChartAfiliados
        :title="'Afiliaciones por mes'"
        :modo="modoChartAfiliados"
        :array="affiliatesByMonth || []"
      />
    </v-card>

  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions } from "vuex";
// Se importan de forma directa. Antes ChartAfiliados se resolvía dentro de un
// `setTimeout(2000)`, lo que dejaba el dashboard sin gráfico durante 2 segundos
// en cada carga sin ningún motivo funcional.
import ChartAfiliados from "../components/base/charts/ChartAfiliados";
import Indicadores from "../components/dashboard/Indicadores";

export default {
  name: "Home",
  components: {
    Indicadores,
    ChartAfiliados,
  },
  data() {
    return {
      modoChartAfiliados: "column",
      items: [
        { title: "Bronce", quantity: 0, icon: "military_tech" },
        { title: "Plata", quantity: 0, icon: "workspace_premium" },
        { title: "Oro", quantity: 0, icon: "emoji_events" },
        { title: "Premium", quantity: 0, icon: "diamond" },
      ],
    };
  },
  created() {
    moment.locale("es");
    this.llamarReportes();
  },
  watch: {
    quantityBronze(q) { this.items[0].quantity = q; },
    quantitySilver(q) { this.items[1].quantity = q; },
    quantityGold(q) { this.items[2].quantity = q; },
    quantityPremium(q) { this.items[3].quantity = q; },
  },
  computed: {
    ...mapState("userNamespace", ["token", "user"]),
    ...mapState("reportNamespace", [
      "quantityBronze",
      "quantitySilver",
      "quantityGold",
      "quantityPremium",
      "affiliatesByMonth",
    ]),
    ...mapState("affiliateNamespace", ["pendingAffiliates"]),
    userName() {
      return this.user && this.user.user ? this.user.user.userName : "";
    },
    rolesUsuario() {
      const role = this.user && this.user.user ? this.user.user.role : null;
      if (Array.isArray(role)) return role;
      if (!role) return [];
      try {
        const parsed = JSON.parse(role);
        return Array.isArray(parsed) ? parsed : [role];
      } catch (e) {
        return [role];
      }
    },
    // El aviso de solicitudes pendientes ofrece un botón que lleva a
    // /pendientes-aprobacion, ruta con `meta.administrador: true`. Sin este
    // chequeo el aviso se le mostraba a cualquier rol que entre al panel (por
    // ejemplo un Embajador), y al hacer click el guard de router.js lo rebotaba
    // a /login, que se lee como si lo hubiera echado del sistema.
    puedeAprobarAfiliaciones() {
      return (
        this.rolesUsuario.includes("Super Admin") ||
        this.rolesUsuario.includes("Administrador")
      );
    },
    saludo() {
      const h = new Date().getHours();
      if (h < 6) return "Buenas noches";
      if (h < 13) return "Buenos días";
      if (h < 20) return "Buenas tardes";
      return "Buenas noches";
    },
    fechaLarga() {
      const f = moment().format("dddd D [de] MMMM [de] YYYY");
      return f.charAt(0).toUpperCase() + f.slice(1);
    },
  },
  methods: {
    ...mapActions("reportNamespace", [
      "getQuantityBronze",
      "getQuantitySilver",
      "getQuantityGold",
      "getQuantityPremium",
      "getAffiliatesByMonth",
    ]),
    ...mapActions("affiliateNamespace", ["getPendingAffiliates"]),
    getRoleColor(role) {
      const colors = {
        "Super Admin": "red darken-2",
        "Administrador": "orange darken-2",
        "Finanzas": "indigo",
        "Editor de Blog": "deep-purple",
        "Vendedor": "green darken-1",
        "Almacenero": "blue-grey",
      };
      return colors[role] || "grey darken-2";
    },
    async llamarReportes() {
      this.getPendingAffiliates();
      await this.getQuantityBronze(this.token);
      await this.getQuantitySilver(this.token);
      await this.getQuantityGold(this.token);
      await this.getQuantityPremium(this.token);
      await this.getAffiliatesByMonth(this.token);
    },
  },
};
</script>

<style scoped>
.lla-home {
  width: 100%;
}

.lla-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.42);
  margin: 4px 0 12px;
}
</style>
